import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ordersService } from "api/services/orders.service";
import ContentHeader from "components/Layout/ContentHeader";
import FulfillmentModal from "components/order-management/FulfillmentModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import Tabs, { TabItem } from "components/UI/Tabs";
import { formatOrderOptions, isDigitalProductOrder } from "utils/order";

import type { AdminOrder } from "api/types/order.types";
import "pages/AdminPage.scss";

const STATUS_LABEL: Record<string, string> = {
  CONFIRMED: "결제완료",
  PENDING: "결제대기",
  PAYMENT_ATTEMPTED: "결제시도",
  PAYMENT_PROCESSING: "결제처리중",
  PAYMENT_FAILED: "결제실패",
  CANCELLED: "취소됨",
  COMPLETED: "완료",
};
const STATUS_BADGE: Record<string, "active" | "pending" | "rejected" | "info" | "draft"> = {
  CONFIRMED: "active",
  PENDING: "pending",
  PAYMENT_ATTEMPTED: "pending",
  PAYMENT_PROCESSING: "info",
  PAYMENT_FAILED: "rejected",
  CANCELLED: "rejected",
  COMPLETED: "active",
};

const FULFILL_LABEL: Record<string, string> = {
  NEW: "신규",
  PREPARING: "배송준비",
  IN_TRANSIT: "배송중",
  DELIVERED: "배송완료",
  PURCHASE_CONFIRMED: "구매확정",
};
const FULFILL_BADGE: Record<string, "active" | "pending" | "info" | "rejected" | "draft"> = {
  NEW: "pending",
  PREPARING: "info",
  IN_TRANSIT: "info",
  DELIVERED: "active",
  PURCHASE_CONFIRMED: "draft",
};

const PAGE_SIZE = 10;

const TABS: TabItem[] = [{ key: "order", label: "주문" }];

const OrderManagement: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("order");
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<AdminOrder | null>(null);
  const [showFulfillModal, setShowFulfillModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchOrders = async (page: number, status: string, q: string, from: string, to: string) => {
    setLoading(true);
    try {
      const res = await ordersService.list({
        page,
        page_size: PAGE_SIZE,
        ...(status ? { status: status as AdminOrder["status"] } : {}),
        ...(q ? { search: q } : {}),
        ...(from ? { date_from: from } : {}),
        ...(to ? { date_to: to } : {}),
      });
      setOrders(res.data.results ?? []);
      setTotal(res.data.count ?? 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(currentPage, statusFilter, search, dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, activeTab, statusFilter]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchOrders(1, statusFilter, search, dateFrom, dateTo);
  };

  const openFulfillModal = (order: AdminOrder) => {
    setSelectedOrder(order);
    setShowFulfillModal(true);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await ordersService.exportExcel({
        ...(statusFilter ? { status: statusFilter as AdminOrder["status"] } : {}),
        ...(search ? { search } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const fallback = "엑셀 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.";
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      let message = fallback;
      try {
        if (data instanceof Blob) {
          const text = await data.text();
          const parsed = JSON.parse(text);
          message = parsed?.error ?? parsed?.detail ?? parsed?.message ?? fallback;
        } else if (data && typeof data === "object") {
          const d = data as { error?: string; detail?: string; message?: string };
          message = d.error ?? d.detail ?? d.message ?? fallback;
        }
      } catch {
        message = fallback;
      }
      alert(message);
    } finally {
      setExporting(false);
    }
  };

  const columns: Column<AdminOrder>[] = [
    { key: "order_number", title: "주문번호" },
    {
      key: "user_name",
      title: "고객명",
      // width: "100px",
      render: (v, row) => {
        return String(v ?? row.user.name);
      },
    },
    {
      key: "order_type",
      title: "유형",
      render: (v, row) => {
        const items = row.items as AdminOrder["items"];
        if (items?.length === 0) return <Badge variant="default">주문</Badge>;
        return <Badge variant="info">장바구니</Badge>;
      },
    },
    {
      key: "items",
      title: "상품명",
      render: (v, row) => {
        const items = row.items as AdminOrder["items"];
        if (items?.length === 0) {
          return row.product_info?.name;
        } else {
          return items.length === 1
            ? items[0].product_name
            : `${items[0].product_name} 외 ${items.length - 1}건`;
        }
      },
    },
    {
      key: "order_options",
      title: "옵션/입력값",
      render: (_, row) => formatOrderOptions(row),
    },
    {
      key: "total_amount",
      title: "결제금액",
      // width: "120px",
      render: (v) => `${Number(v).toLocaleString()}원`,
    },
    { key: "created_at", title: "주문일", width: "120px", render: (v) => String(v).slice(0, 10) },
    {
      key: "status",
      title: "결제상태",
      render: (v) => <Badge variant={STATUS_BADGE[String(v)] ?? "draft"}>{STATUS_LABEL[v]}</Badge>,
    },
    {
      key: "fulfillment_status",
      title: "배송상태",
      // width: "110px",
      render: (v) => (
        <Badge variant={FULFILL_BADGE[String(v)] ?? "draft"}>
          {FULFILL_LABEL[String(v)] ?? "-"}
        </Badge>
      ),
    },
    {
      key: "actions",
      title: "",
      // width: "100px",
      render: (_, row) => {
        const canManageFulfillment =
          row.status === "CONFIRMED" &&
          !isDigitalProductOrder(row) &&
          (row.fulfillment_status == null ||
            row.fulfillment_status === "NEW" ||
            row.fulfillment_status === "PREPARING");
        if (!canManageFulfillment) return null;
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              openFulfillModal(row);
            }}
          >
            배송 관리
          </Button>
        );
      },
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="제품 주문 관리"
        description="고객의 모든 주문 내역을 조회하고 배송 상태를 관리합니다."
        actions={[
          <Button key="export" variant="outline" onClick={handleExportExcel} disabled={exporting}>
            {exporting ? "다운로드 중..." : "엑셀 다운로드"}
          </Button>,
        ]}
      />

      <div className="admin-page__card">
        <Tabs
          items={TABS}
          activeKey={activeTab}
          onChange={(k) => {
            setActiveTab(k);
            setCurrentPage(1);
          }}
        />

        <div className="admin-page__filter">
          <Input
            placeholder="주문번호, 고객명 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ width: 260 }}
            prefix="🔍"
          />
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: "", label: "결제상태 전체" },
              { value: "CONFIRMED", label: "결제완료" },
              { value: "PENDING", label: "결제대기" },
              { value: "PAYMENT_FAILED", label: "결제실패" },
              { value: "CANCELLED", label: "취소됨" },
              { value: "COMPLETED", label: "완료" },
            ]}
            style={{ width: 140 }}
          />
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
              }}
            />
            <span style={{ color: "#71717A" }}>~</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
              }}
            />
            <Button variant="secondary" size="sm" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div>
        ) : (
          <Table
            columns={columns}
            data={orders}
            rowKey="id"
            onRowClick={(row) => navigate(`/order-management/${row.id}`)}
          />
        )}
        <Pagination
          current={currentPage}
          total={total}
          pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      </div>

      <FulfillmentModal
        open={showFulfillModal}
        order={selectedOrder}
        onClose={() => setShowFulfillModal(false)}
        onUpdated={() => fetchOrders(currentPage, statusFilter, search, dateFrom, dateTo)}
      />
    </div>
  );
};

export default OrderManagement;
